/**
 * Admin · Prompt template editor — full page.
 *
 * Edits the Jinja2 body and `is_active`. The (name, scope, key) tuple
 * is immutable per backend schema (`PromptTemplateUpdate` only accepts
 * `template_jinja` and `is_active`).
 *
 * Shows the bumped `version` after save so admins can see history is
 * being tracked.
 */

import { useCallback, useEffect, useState } from "react";
import { AlertCircle, ArrowLeft, Trash2 } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Badge, Button } from "@omnitok/ui";
import { Spinner } from "@pervasivemind/bi-components";

import { HttpError } from "@/lib/api";
import {
  promptsApi,
  type PromptTemplate,
} from "@/lib/prompts-api";
import { useT } from "@/i18n";

export function PromptEditPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useT();
  const [row, setRow] = useState<PromptTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [templateJinja, setTemplateJinja] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const reload = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const fetched = await promptsApi.get(id);
      setRow(fetched);
      setTemplateJinja(fetched.template_jinja);
      setIsActive(fetched.is_active);
    } catch (err) {
      const message = err instanceof HttpError ? err.message : String(err);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const dirty =
    row !== null &&
    (templateJinja !== row.template_jinja || isActive !== row.is_active);

  async function handleSave(): Promise<void> {
    if (!row || !dirty || saving) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await promptsApi.update(row.id, {
        template_jinja: templateJinja,
        is_active: isActive,
      });
      setRow(updated);
      setTemplateJinja(updated.template_jinja);
      setIsActive(updated.is_active);
      setSavedAt(Date.now());
    } catch (err) {
      const message = err instanceof HttpError ? err.message : String(err);
      setError(message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(): Promise<void> {
    if (!row) return;
    if (!window.confirm(t("admin.prompts.deleteConfirm"))) return;
    try {
      await promptsApi.remove(row.id);
      navigate("/admin/prompts");
    } catch (err) {
      const message = err instanceof HttpError ? err.message : String(err);
      setError(message);
    }
  }

  if (loading) {
    return (
      <div className="flex h-[70vh] items-center justify-center text-sm text-neutral-500">
        <Spinner size={16} className="mr-2" ariaLabel={t("admin.common.loading")} />
        {t("admin.common.loading")}
      </div>
    );
  }
  if (!row) {
    return (
      <div className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-6">
        <div className="flex items-center gap-2 rounded-lg border border-error/40 bg-error/5 px-4 py-2 text-sm text-error-dark">
          <AlertCircle size={14} /> {error ?? t("admin.prompts.notFound")}
        </div>
      </div>
    );
  }

  return (
    <div
      className="mx-auto w-full max-w-4xl space-y-4 px-4 py-8 sm:px-6"
      onKeyDown={(e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "s") {
          e.preventDefault();
          void handleSave();
        }
      }}
    >
      <Link
        to="/admin/prompts"
        className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-900"
      >
        <ArrowLeft size={12} /> {t("admin.common.back")}
      </Link>

      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-mono text-2xl font-semibold text-neutral-900">
            {row.name}
          </h1>
          <p className="mt-1 flex items-center gap-2 text-sm text-neutral-500">
            <Badge variant="neutral" size="sm" pill>
              {row.scope}
            </Badge>
            v{row.version} · {row.report_draft_id ?? row.client_key ?? "—"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-neutral-700">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4 rounded border-neutral-300"
            />
            {t("admin.prompts.activeLabel")}
          </label>
          <Button
            variant="primary"
            size="sm"
            onClick={() => void handleSave()}
            disabled={!dirty || saving}
            loading={saving}
          >
            {t("admin.common.save")}
          </Button>
          <button
            type="button"
            onClick={() => void handleDelete()}
            className="grid h-9 w-9 place-items-center rounded-md text-neutral-500 transition-colors hover:bg-error/10 hover:text-error-dark"
            title={t("admin.common.delete")}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </header>

      {error ? (
        <div className="flex items-center gap-2 rounded-lg border border-error/40 bg-error/5 px-4 py-2 text-sm text-error-dark">
          <AlertCircle size={14} /> {error}
        </div>
      ) : null}

      <textarea
        rows={28}
        value={templateJinja}
        onChange={(e) => setTemplateJinja(e.target.value)}
        className="block w-full rounded-[10px] border border-control bg-white px-3 py-2 placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary min-h-[60vh] font-mono text-xs"
      />

      <p className="text-xs text-neutral-500">
        {savedAt !== null && !dirty
          ? t("admin.prompts.savedHint", { version: row.version })
          : t("admin.prompts.shortcutHint")}
      </p>
    </div>
  );
}
